import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Masonry,
  usePositioner,
  useResizeObserver,
} from 'masonic';
import { useIntl } from 'react-intl';

import FilesMasonryItem from './MasonryItem';

import withDragAndDropContext from '../../helpers/withDragAndDropContext';

const FilesMasonry = ({
  files,
  selectedFiles,
  filesLastUpdate,
  reorderingMode,
  sidebarOpen,
  handleMoveSelectedItems,
  className,
  share,
  shareCollectionUuid,
  displayOnly,
  allowCheck,
  renderDisplayAction,
}) => {
  const [width, setWidth] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(filesLastUpdate);
  const intl = useIntl();

  const photos = files.map(file => (
    {
      file,
      isSelected: !!selectedFiles.find(sf => (sf.uuid === file.uuid)),
      share,
      shareCollectionUuid,
      handleMoveSelectedItems,
      displayOnly,
      allowCheck,
      renderDisplayAction,
    }
  ));

  const containerRef = React.useRef(null);
  const positioner = usePositioner(
    { width, columnGutter: 8 },
    [lastUpdate, filesLastUpdate, photos.length],
  );
  const resizeObserver = useResizeObserver(positioner);

  const observer = useRef(
    new ResizeObserver((entries) => {
      const { width: containerWidth } = entries[0].contentRect;
      setWidth(containerWidth);
    }),
  );

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.getBoundingClientRect().width);
    }
  }, [lastUpdate]);

  useEffect(() => {
    if (containerRef.current) {
      observer.current.observe(containerRef.current);
    }
  }, [observer, lastUpdate]);

  useEffect(() => {
    setTimeout(() => {
      setLastUpdate(new Date());
    }, 500);
  }, [sidebarOpen]);

  if (photos.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <div className={classnames('files-masonry', className)}>
        {reorderingMode && (
          <p className="notice">
            {intl.formatMessage({ defaultMessage: 'Switch to grid or list view to reorder files.' })}
          </p>
        )}
        <Masonry
          key={`${lastUpdate}-${filesLastUpdate}-${photos.length}`}
          className="masonry-grid"
          positioner={positioner}
          resizeObserver={resizeObserver}
          items={photos}
          render={FilesMasonryItem}
          overscanBy="Infinity"
          columnGutter={20}
          columnWidth={280}
        />
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  selectedFiles: state.Files.selectedFiles,
  filesLastUpdate: state.Files.lastUpdate,
  reorderingMode: state.Files.reorderingMode,
  sidebarOpen: state.App.secondSidebarOpen,
});

FilesMasonry.propTypes = {
  selectedFiles: PropTypes.array.isRequired,
  filesLastUpdate: PropTypes.string.isRequired,
  reorderingMode: PropTypes.bool.isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  files: PropTypes.array.isRequired,
  share: PropTypes.object,
  shareCollectionUuid: PropTypes.string,
  className: PropTypes.string,
  handleMoveSelectedItems: PropTypes.func,
  displayOnly: PropTypes.bool,
  allowCheck: PropTypes.bool,
  renderDisplayAction: PropTypes.func,
};

FilesMasonry.defaultProps = {
  share: null,
  shareCollectionUuid: null,
  className: '',
  displayOnly: false,
  allowCheck: true,
  handleMoveSelectedItems: () => {},
  renderDisplayAction: null,
};

export default withDragAndDropContext(connect(mapStateToProps)(FilesMasonry));
